import type { NaceDivisionCode } from "./practice-catalog.ts";
export type { NaceDivisionCode };

import type { AuditInputType } from "./utils/audit-input-type.ts";
export type { AuditInputType };

import type { SectorsDisagreement } from "./pipeline-transparency.ts";
export type { SectorsDisagreement };

import type { LegalBasisStringEntry } from "./report/legal-basis-strings.ts";
import type {
  AssessmentScopeStatus,
  CanonicalReportFinding,
  ClaimEvidenceEvaluationState,
  ClaimVerdictState,
  CorrectedDocumentReceipt,
  CorrectedDocumentStatus,
  DocumentEvidenceSummary,
  EvidenceMatchFailureReason,
  OutputProvenance,
  OverallAssessmentWireValue,
  ReportLegalConclusion,
  RewriteValidationState,
  Section10RewriteState,
  SourceAnchorState,
} from "./report/integrity.ts";
export type {
  AssessmentScopeStatus,
  CanonicalReportFinding,
  ClaimEvidenceEvaluationState,
  ClaimVerdictState,
  CorrectedDocumentReceipt,
  CorrectedDocumentStatus,
  DocumentEvidenceSummary,
  EvidenceMatchFailureReason,
  OutputProvenance,
  OverallAssessmentWireValue,
  ReportLegalConclusion,
  RewriteValidationState,
  Section10RewriteState,
  SourceAnchorState,
};

/**
 * Risk level classification for claims and audits (legacy)
 */
export type RiskLevel = "RED" | "YELLOW" | "GREEN";

/**
 * Orthogonal semantic reason codes for claim language (spec
 * docs/specs/risk-assessment-deterministic-classification.md, Addendum
 * 2026-08-24). Describe the claim's wording (vague/aspirational vs
 * comparative) WITHOUT deciding risk, practice type, severity, or legal
 * basis - those continue to derive only from the Krok 0-4 tree
 * (violationCategories stays the authoritative legal-path list).
 */
export type SemanticClaimCategory =
  | "VAGUE_TERMINOLOGY"
  | "COMPARATIVE_MISLEADING";

/**
 * RAG retrieval status for claim citations
 */
export type RagStatus = "pending" | "running" | "complete" | "timed_out";

/**
 * Practice type classification (BLACK/GREY framework)
 * - BLACK: Always prohibited practices (Annex I UCPD)
 * - GREY: Context-dependent practices requiring assessment
 * - OTHER: Other risks not falling into BLACK/GREY categories
 * - COMPLIANT: No significant risk identified
 */
export type PracticeType = "BLACK" | "GREY" | "OTHER" | "COMPLIANT";

/**
 * Severity level within practice type
 */
export type PracticeSeverity = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

/**
 * Violation category taxonomy
 * Maps to specific greenwashing patterns from methodology
 *
 * Legacy field for backward compatibility, superseded by
 * `Claim.violationCategories` (pirx-z0mic.7, risk-assessment-precision-
 * lost-downstream). Kept until T9's writer cutover.
 */
export type ViolationCategory =
  | "ABSOLUTE_CLAIM" // "fully", "100%", "exclusively" without proof
  | "VAGUE_TERMINOLOGY" // "green", "eco", "sustainable" without specifics
  | "FALSE_CERTIFICATION" // unauthorized labels or certifications
  | "HIDDEN_TRADEOFF" // omitting negative environmental aspects
  | "FUTURE_WITHOUT_PLAN" // future claims without implementation roadmap
  | "COMPARATIVE_MISLEADING" // unfair competitor comparisons
  | "SCOPE_MISMATCH" // whole product claims when only part applies
  | "PROOF_MISSING" // unverifiable assertions
  | "TRUST_MARK_ABUSE" // misuse of quality marks or trust symbols
  | "OFFSETTING_DECEPTION" // misleading carbon offset claims
  | "ENDORSEMENT_FRAUD" // fake endorsements or testimonials
  | "IRRELEVANT_CLAIM"; // technically true but irrelevant claims

/**
 * Modification type for text changes in recommendations
 */
export type ModificationType = "replace" | "add" | "remove";

/**
 * Single text modification with original and replacement text
 */
export interface TextModification {
  type: ModificationType;
  original: string;
  replacement: string;
  rationale: string;
}

/**
 * Citation linking a claim to legal/methodological sources
 */
export interface Citation {
  id: string;
  claimId: string;
  sourceDocument: string;
  articleReference: string;
  frameworkName?: string;
  relevanceScore?: number;
}

/**
 * Individual environmental claim extracted from document
 */
export interface Claim {
  id: string;
  auditId: string;
  claimText: string;
  positionStart: number;
  positionEnd: number;
  riskLevel: RiskLevel;
  ragStatus: RagStatus;
  violationType?: string;
  recommendationText?: string;
  saferPhrasing?: string;
  rationale?: string;
  citations: Citation[];
  // Phase 2: Evidence verification fields
  evidenceMatches?: ClaimEvidenceMatch[];
  evidenceGaps?: EvidenceGap[];
  // Enhanced classification fields (golden output quality)
  practiceType?: PracticeType;
  practiceSeverity?: PracticeSeverity;
  // Legacy field for backward compatibility, superseded by
  // violationCategories below. T9 has landed (the tree-driven writer,
  // audit-claim-processing.ts): the backing violation_category DB column
  // was dropped by migration 0092 and no writer populates this field
  // anymore. Kept on the wire type only for existing API consumers that
  // may still read it; always undefined/null on new claims.
  violationCategory?: ViolationCategory | null;
  // Canonical, array-shaped replacement for violationCategory above
  // (pirx-z0mic.7, risk-assessment-precision-lost-downstream). `practiceId`
  // stays a plain `string`, not narrowed to the canonical `BlackPracticeId`
  // (re-exported below from ./practice-catalog.ts): the backend's
  // `practiceIdForLegalBasis` (risk-decision-tree/violation-categories.ts)
  // also emits synthetic KROK 0/2/3 ids and KROK 4 `GreyPracticeId`
  // values, so this field is never exclusively a `BlackPracticeId`.
  // Populated by the risk-decision-tree writer (audit-claim-processing.ts);
  // undefined/empty only for claims written before that writer landed.
  violationCategories?: Array<
    { practiceId: string; legalBasis: LegalBasisStringEntry[] }
  >;
  legalBasis?: string;
  // Enhanced recommendation fields
  modifications?: TextModification[];
  reformulatedMessage?: string;
  // Consumer perspective analysis
  consumerPerspective?: string;
  // CSR-mode per-claim card fields (pirx-cijf6.4 Phase 1 backend), already
  // computed and sent by backend/src/services/audits/audit.ts's
  // buildCsrClaimCardFields -- these fields declare the existing wire
  // contract, no new backend behavior (pirx-tbjyi).
  subtype?: "no-impact" | "comparative" | "trend" | "none";
  basis?: "legal" | "reputational";
  checkStatus?: "step0-out" | "not-a-claim" | "step4-no-grounds" | "scored";
  // Audit-output-integrity boundary (docs/specs/audit-output-integrity-
  // consistency-and-provenance.md): discriminated integrity states for
  // this claim's source anchor, final verdict, evidence evaluation, and
  // per-claim rewrite-safety receipt. Absent (pre-cutover) means the
  // backend has not yet emitted this state -- callers MUST treat absence
  // identically to the type's own "legacy/unverified/unavailable" member,
  // never inferring a verified value from the legacy scalar fields above.
  sourceAnchorState?: SourceAnchorState;
  claimVerdictState?: ClaimVerdictState;
  claimEvidenceEvaluationState?: ClaimEvidenceEvaluationState;
  rewriteValidationState?: RewriteValidationState;
}

/**
 * Complete audit report for a document
 */
export interface Audit {
  id: string;
  userId?: string;
  documentName: string;
  // Cosmetic display name, distinct from `documentName` (which also serves
  // as the download filename). Null for rows created before this field
  // landed and for untitled paste-text submissions -- fall back to
  // `documentName` when null.
  displayName?: string | null;
  // Original URL for a website scan; null for uploads and pasted text. Also
  // the input-type discriminator (see `inputType`/`getAuditInputType`).
  sourceUrl?: string | null;
  // "website" (scanned URL) vs. "document" (upload or pasted text).
  inputType?: AuditInputType;
  gcsFilePath?: string;
  overallRiskLevel: RiskLevel | null;
  status?: AuditStatus;
  createdAt: string;
  updatedAt?: string;
  claims: Claim[];
  // Phase 2: Evidence verification fields
  evidenceSufficiencyScore?: number; // 0-100 evidence quality score
  evidenceDocuments?: EvidenceDocument[];
  // Corrected document (populated when CORRECTED_TEXT_OUTPUT is enabled)
  correctedText?: string | null;
  // Language the report/claims were generated in (from audit context).
  language?: "pl" | "en";
}

/**
 * Paginated list of audits
 */
export interface AuditList {
  audits: Array<{
    id: string;
    documentName: string;
    overallRiskLevel: RiskLevel;
    createdAt: string;
  }>;
  page: number;
  limit: number;
}

/**
 * Error response from API
 */
export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
  /**
   * Present on URL-scan fail-loud responses (pirx-27tn): the taxonomy code the
   * frontend maps to an i18n message. Absent on all other errors.
   */
  failureCode?: UrlScanFailureCode;
}

/**
 * Upload file request
 */
export interface UploadRequest {
  file: File | Blob;
  fileName: string;
}

/**
 * Upload audit response
 */
export interface UploadResponse {
  auditId: string;
  status: "processing" | "completed" | "failed";
  overallRiskLevel: RiskLevel;
  totalClaims: number;
  flaggedClaims: number;
  insufficientEvidenceClaims: number;
  message?: string;
}

// Phase 2: Evidence Upload & Verification Types

/**
 * Evidence document parsing status
 */
export type ParsingStatus = "pending" | "processing" | "complete" | "failed";

/**
 * Claim-evidence alignment score
 */
export type AlignmentScore = "STRONG" | "PARTIAL" | "WEAK" | "MISSING";

/**
 * Evidence gap severity level
 */
export type GapSeverity = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

/**
 * Evidence gap type classification
 */
export type GapType =
  | "missing_evidence"
  | "weak_evidence"
  | "temporal_mismatch"
  | "scope_mismatch"
  | "vague_evidence";

/**
 * Extracted data from evidence documents (stored in JSONB)
 */
export interface ParsedEvidenceData {
  metrics?: Array<{
    name: string;
    value: number;
    unit: string;
    year?: number;
  }>;
  statements?: string[];
  temporalInfo?: {
    reportingPeriod?: string;
    baselineYear?: number;
    targetYear?: number;
  };
  scope?: {
    productLines?: string[];
    geographies?: string[];
    operations?: string[];
  };
}

/**
 * Quantitative values extracted from evidence (stored in JSONB)
 */
export interface QuantitativeValues {
  values?: Array<{
    metric: string;
    value: number;
    unit: string;
  }>;
}

/**
 * Evidence document uploaded for verification
 */
export interface EvidenceDocument {
  id: string;
  auditId: string;
  storagePath: string;
  filename: string;
  fileSize: number;
  mimeType: string;
  parsingStatus: ParsingStatus;
  parsedData?: ParsedEvidenceData;
  createdAt: string;
  updatedAt: string;
}

/**
 * Match between a claim and supporting evidence
 */
export interface ClaimEvidenceMatch {
  id: string;
  claimId: string;
  evidenceDocumentId: string;
  alignmentScore: AlignmentScore;
  confidence: number; // 0-1
  evidencePassage: string;
  quantitativeValues?: QuantitativeValues;
  temporalMatch?: string;
  scopeMatch?: string;
  createdAt: string;
  evidenceDocument?: EvidenceDocument; // Populated via relation
}

/**
 * Detected gap in evidence for a claim
 */
export interface EvidenceGap {
  id: string;
  claimId: string;
  gapType: GapType;
  severity: GapSeverity;
  recommendation: string;
  rationale: string;
  createdAt: string;
}

// Phase 3: Billing Types

/**
 * Subscription plan interval
 */
export type PlanInterval = "month" | "year";

/**
 * Subscription status
 */
export type SubscriptionStatus =
  | "active"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "trialing"
  | "unpaid";

/**
 * Billing mode for a user/account
 */
export type BillingMode = "subscription" | "prepaid" | "metered";

/**
 * Order status for one-time purchases
 */
export type OrderStatus = "pending" | "succeeded" | "failed";

/**
 * Plan entitlement types
 */
export type EntitlementType =
  | "audits_per_month"
  | "unlimited_audits"
  | "evidence_verification"
  | "priority_support";

/**
 * Plan entitlement value
 */
export interface PlanEntitlement {
  type: EntitlementType;
  value: number | boolean;
}

/**
 * Price tier for a subscription plan
 */
export interface PriceTier {
  id: string;
  name: string;
  interval: PlanInterval;
  amount: number; // Amount in cents (EUR)
}

/**
 * Subscription product/plan
 */
export interface SubscriptionProduct {
  id: string;
  name: string;
  description: string;
  currency: "eur" | "pln";
  highlighted?: boolean;
  badge?: string;
  prices: PriceTier[];
  entitlements?: PlanEntitlement[];
  features: string[];
}

/**
 * One-time purchase product
 */
export interface OneTimePurchase {
  id: string;
  name: string;
  description: string;
  currency: "eur" | "pln";
  amount: number; // Amount in the smallest currency unit (grosze for PLN, cents for EUR)
  credits: number;
}

/**
 * User's billing account
 */
export interface BillingAccount {
  id: string;
  companyName?: string | null;
  email?: string | null;
  vatId?: string | null;
  hasStripeCustomer: boolean;
}

/**
 * User's subscription
 */
export interface Subscription {
  id: string;
  stripePriceId: string;
  status: SubscriptionStatus;
  currency: string;
  periodStartsAt?: string | null;
  periodEndsAt?: string | null;
  cancelAtPeriodEnd: boolean;
  plan?: {
    productId: string;
    productName: string;
    priceId: string;
    priceName: string;
    interval: PlanInterval;
    amount: number;
    entitlements: PlanEntitlement[];
  } | null;
}

/**
 * One-time purchase order
 */
export interface Order {
  id: string;
  status: OrderStatus;
  currency: string;
  totalAmount: number;
  invoicePdfUrl?: string | null;
  createdAt: string;
}

/**
 * Response from GET /api/billing/plans
 */
export interface BillingPlansResponse {
  subscriptions: SubscriptionProduct[];
  oneTimePurchases: OneTimePurchase[];
}

/**
 * Response from GET /api/billing/account
 */
export interface BillingAccountResponse {
  hasAccount: boolean;
  account?: BillingAccount | null;
  subscription?: Subscription | null;
  creditBalanceUpdatedAt?: string | null;
  billingMode?: BillingMode | null;
}

/**
 * Request for POST /api/billing/account
 */
export interface CreateBillingAccountRequest {
  companyName?: string;
  email: string;
  billingAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  vatId?: string;
}

/**
 * Response from POST /api/billing/account
 */
export interface CreateBillingAccountResponse {
  id: string;
  companyName?: string | null;
  email?: string | null;
  vatId?: string | null;
  hasStripeCustomer: boolean;
}

/**
 * Request for POST /api/billing/checkout
 */
export interface CreateCheckoutRequest {
  productId?: string;
  priceId?: string;
  purchaseId?: string;
}

/**
 * Response from POST /api/billing/checkout
 */
export interface CreateCheckoutResponse {
  sessionId: string;
  checkoutUrl: string;
}

/**
 * Request for POST /api/billing/portal
 */
export type CreatePortalRequest = Record<string, never>;

/**
 * Response from POST /api/billing/portal
 */
export interface CreatePortalResponse {
  portalUrl: string;
}

// Phase 3: Compliant Communication Generation Types

/**
 * Tone level for generated messaging
 */
export type MessageTone = "conservative" | "balanced" | "confident";

/**
 * Evidence citation in generated messaging
 */
export interface MessagingEvidenceCitation {
  evidenceDocumentId: string;
  filename: string;
  passage: string;
  dataPoints?: Record<string, string | number>;
}

/**
 * Single generated messaging alternative
 */
export interface GeneratedMessageAlternative {
  tone: MessageTone;
  text: string;
  evidenceCitations: MessagingEvidenceCitation[];
  complianceNotes: string;
}

/**
 * Generated messaging for a single claim
 */
export interface GeneratedClaimMessage {
  claimId: string;
  originalClaim: string;
  riskLevel: RiskLevel;
  alternatives: GeneratedMessageAlternative[];
  gaps: Array<{
    gapType: GapType;
    severity: GapSeverity;
    recommendation: string;
  }>;
}

/**
 * Request for POST /api/audits/:auditId/generate-messaging
 */
export interface GenerateMessagingRequest {
  claimIds?: string[];
  options?: {
    toneLevel?: MessageTone;
    includeDataPoints?: boolean;
    maxLength?: number;
  };
}

/**
 * Response from POST /api/audits/:auditId/generate-messaging
 */
export interface GenerateMessagingResponse {
  auditId: string;
  generatedMessages: GeneratedClaimMessage[];
}

// Phase 6: Audit History & Exports Types

/**
 * Audit status for filtering
 */
export type AuditStatus =
  | "UPLOADING"
  | "QUEUED"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED";

/**
 * Current pipeline stage label for an in-progress audit (pirx-qydoj). All 10
 * values across both phases, verbatim from where the pipelines already use
 * them internally — never freeform/human-readable text at the data layer:
 * the 4 methodology stages match `PipelineStage`
 * (backend/src/services/methodology/types/pipeline.ts) and the 6 assessment
 * stages match the stage list in
 * backend/src/services/assessments/assessment-pipeline.ts. The frontend's
 * label lookup is a closed map keyed by these exact strings.
 */
export type AuditProcessingStage =
  | "claim_extraction"
  | "dedup"
  | "batch_query"
  | "report_merge"
  | "normalize_input"
  | "dual_retrieval"
  | "report_generation"
  | "review_packet"
  | "report_rendering"
  | "finalize";

/**
 * Sort field for audit list
 */
export type AuditSortField = "createdAt" | "riskLevel" | "documentName";

/**
 * Sort order
 */
export type SortOrder = "asc" | "desc";

/**
 * Parameters for listing audits with pagination and filters
 */
export interface AuditListParams {
  page?: number;
  limit?: number;
  sortBy?: AuditSortField;
  sortOrder?: SortOrder;
  riskLevel?: RiskLevel;
  status?: AuditStatus;
  inputType?: AuditInputType;
  dateFrom?: string; // ISO date
  dateTo?: string; // ISO date
  userId?: string;
  /** Free-form text matched against document/display name, source URL, and audit metadata (industry sector, communication purpose). */
  q?: string;
}

/**
 * Audit item in list view (summary, not full details)
 */
export interface AuditListItem {
  id: string;
  documentName: string;
  displayName?: string | null;
  sourceUrl?: string | null;
  inputType?: AuditInputType;
  overallRiskLevel: RiskLevel | null;
  evidenceSufficiencyScore: number | null;
  status: AuditStatus;
  createdAt: string;
  updatedAt: string;
  teamId?: string;
  teamName?: string;
  // Language the report was generated in (from audit context); defaults pl.
  language?: "pl" | "en";
  // Owner user id — only populated by the operator/global listing
  // (GET /api/admin/audits), undefined for the per-user endpoint.
  userId?: string;
  // Owner email — only populated by the operator/global listing.
  ownerEmail?: string | null;
  // Current pipeline stage (pirx-qydoj) — present only while status is
  // PROCESSING; absent/null for terminal or pre-processing rows, and absent
  // for any row whose write raced or hasn't landed yet. Never treat a
  // missing value as a stuck pipeline.
  currentStep?: AuditProcessingStage | null;
  // ISO timestamp the current processing lease started (audits.processing_started_at).
  // Drives the elapsed-time readout; same absence rules as currentStep.
  processingStartedAt?: string | null;
}

/**
 * Response from GET /api/audits with pagination
 */
export interface AuditListResponse {
  audits: AuditListItem[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Options for JSON export
 */
export interface AuditExportOptions {
  includeRawText?: boolean;
}

// Phase 7: Onboarding, Knowledge Base, Assessments Types

/**
 * Team membership summary for the current user
 */
export interface TeamInfo {
  id: string;
  name: string;
  role: string;
}

/**
 * Company data captured during onboarding
 */
export type IndustrySectorSelection = string[] | string;

export interface CompanyData {
  business_activity: string;
  company_name: string;
  legal_form: string;
  industry_sector: IndustrySectorSelection;
  industry_sector_other?: string;
  nip?: string;
}

/**
 * Current user profile response
 */
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
  teams: TeamInfo[];
  onboardingCompleted: boolean;
  companyData: CompanyData | null;
  /** UI language preference (pl|en). Defaults to "pl" for legacy rows. */
  appLanguage?: "pl" | "en";
  /** Generated-report language preference (pl|en). Defaults to "pl". */
  reportLanguage?: "pl" | "en";
  /** True for internal/team accounts only (docs/specs/settings-internal-testing-tools.md).
   * UX signal for showing the Settings "Testing tools" tab — never a security
   * boundary; every internal endpoint re-checks the allowlist server-side. */
  isInternalTester?: boolean;
}

/**
 * Request payload for onboarding completion
 */
export interface OnboardingRequest {
  businessActivity: string;
  companyName: string;
  legalForm: string;
  industrySector: IndustrySectorSelection;
  industrySectorOther?: string;
  nip: string;
}

/**
 * Optional company data override for assessment creation.
 * Uses snake_case because it is forwarded directly to backend assessment schema.
 */
export interface AssessmentCompanyDataOverride {
  business_activity: string;
  company_name: string;
  legal_form: string;
  industry_sector: IndustrySectorSelection;
  industry_sector_other?: string;
}

/**
 * Response from POST /api/users/onboarding
 */
export interface OnboardingResponse {
  user: UserProfile;
}

/**
 * Knowledge base file metadata
 */
export interface KnowledgeBaseFile {
  id: string;
  filename: string;
  mimeType: string;
  fileSizeBytes: number;
  status: "indexing" | "ready";
  createdAt: string;
}

/**
 * Response from GET /api/knowledge-base
 */
export interface KnowledgeBaseListResponse {
  files: KnowledgeBaseFile[];
  quota: {
    fileCount: number;
    maxFiles: number;
    totalSize: number;
    maxSize: number;
  };
}

/**
 * Response from DELETE /api/knowledge-base/:id
 */
export interface KnowledgeBaseDeleteResponse {
  success: boolean;
  deletedFileId: string;
}

/**
 * Assessment status values
 */
export type AssessmentStatus = "queued" | "processing" | "completed" | "failed";
export type AssessmentStatusInProgress = "queued" | "processing";

/**
 * Request for POST /api/assessments
 */
export interface AssessmentCreateRequest {
  companyDataOverride?: AssessmentCompanyDataOverride;
  teamId?: string;
}

/**
 * Response from POST /api/assessments
 */
export interface AssessmentCreateResponse {
  assessmentId: string;
  status: "queued";
  estimatedTimeSeconds: number;
}

/**
 * Common assessment status fields shared across all statuses.
 */
export interface AssessmentStatusCommon {
  id: string;
  progress: number;
  createdAt: string;
  /** Audit ID if assessment is linked to a specific audit */
  auditId?: string;
  /**
   * Optional internal processing step identifier.
   *
   * NOTE: This may be omitted by the backend; clients must handle undefined.
   */
  currentStep?: string;
}

/**
 * In-progress assessment response (queued/processing)
 */
export interface AssessmentStatusBase extends AssessmentStatusCommon {
  status: AssessmentStatusInProgress;
}

/**
 * Completed assessment response
 */
export interface AssessmentStatusCompleted extends AssessmentStatusCommon {
  status: "completed";
  report: {
    sections: Record<string, unknown>;
    markdown?: string;
    pdfUrl?: string | null;
    processingTimeMs?: number | null;
    /**
     * Article 50(2) AI Act machine-readable content-provenance marker.
     * Always true for a completed report — sourced from
     * `AI_PROVENANCE.aiGenerated`, never a hardcoded literal at the call
     * site. See docs/plans/2026-07-27-ai-act-report-content-marking-spec.md.
     */
    aiGenerated: boolean;
    /**
     * Build/version-stamp: the git SHA the report was built from, for the
     * "App version: {hash}" footer sentence composed by
     * `report/build-provenance.ts`'s `getAppVersionText`. `null`/absent
     * when the build metadata wasn't available at generation time —
     * optional (unlike `aiGenerated` here) because the backend response
     * construction that populates it lands in a separate task and existing
     * completed-report payloads predate this field. See
     * docs/specs/report-build-version-stamp.md.
     */
    gitSha?: string | null;
    /**
     * Present only when the commercial-practice scope gate took the audit out
     * of scope (byh4n.9; `overall_assessment === "NOT_APPLICABLE"`). Lets the UI
     * render a distinct out-of-scope state — never "safe/compliant" — and
     * surface the declared-vs-content disagreement flag. Absent for analyzed
     * (in-scope) reports.
     */
    outOfScope?: {
      reason: PreScreeningReason;
      declaredPurpose?: CommunicationPurpose;
      purposeDisagreement?: boolean;
    };
    /**
     * Display-only purpose-disagreement signal for IN-SCOPE (analyzed)
     * reports — `overall_assessment` is `WYSTĘPUJE`/`NIE WYSTĘPUJE`, the
     * commercial-practice risk assessment ran normally, and the declared
     * CSR/educational purpose disagreed with the pipeline's own
     * content-vs-context reading (pirx-teiyf, operator decision
     * 2026-08-23: "downstream-only relabel, don't re-score"). Never binds
     * scope or severity — purely a report-level annotation. Absent when
     * the report is out-of-scope (see `outOfScope` above, which already
     * carries this same signal) or when no disagreement was detected.
     */
    scopeGate?: {
      declaredPurpose?: CommunicationPurpose;
      purposeDisagreement: boolean;
    };
    /**
     * Canonical additive integrity fields
     * (docs/specs/audit-output-integrity-consistency-and-provenance.md).
     * Every *new* producer run sets these from the final-canonicalized
     * report; historical rows predating this spec omit them entirely.
     * Consumers MUST treat an absent field as "not recorded", never infer
     * a clean/verified state from its absence.
     */
    /** Derived first from the authoritative pre-screening/KROK 0 result. */
    assessmentScopeStatus?: AssessmentScopeStatus;
    /**
     * The exact paired conclusion for `overallAssessmentWireValue` below —
     * confirmed_practice/WYSTĘPUJE, qualified_risk/QUALIFIED_RISK,
     * no_reportable_risk/NIE WYSTĘPUJE, not_applicable/NOT_APPLICABLE. No
     * other pairing is ever produced.
     *
     * `null` (present but null, distinct from absent) means this row's
     * output provenance is not sealed/verifiable — the read-side integrity
     * gate (docs/specs/audit-output-integrity-consistency-and-provenance.md)
     * projects the atomic pair as null/null rather than exposing an
     * unverified value. Absent entirely means a legacy row predating this
     * spec that never had the columns at all. Consumers MUST NOT treat
     * `null` and absent as the same "safe to ignore" case if they display
     * a distinct "unverified historical report" state.
     */
    reportLegalConclusion?: ReportLegalConclusion | null;
    /**
     * The exact canonical `overall_assessment` wire value paired 1:1 with
     * `reportLegalConclusion` above. Distinct from the untyped
     * `sections.overall_assessment` bag so clients get compile-time
     * exhaustiveness on the four wire values instead of `unknown`. See
     * `reportLegalConclusion` above for the null-vs-absent distinction.
     */
    overallAssessmentWireValue?: OverallAssessmentWireValue | null;
    /**
     * One canonical report finding per reportable claim outcome, carrying
     * the required `claimId`/`claimGroupId`/`outcomeId` lineage. A report
     * finding is never re-derived by matching a card to a heading/color by
     * text.
     */
    canonicalFindings?: CanonicalReportFinding[];
    /**
     * Audit-level evidence-input status plus the two-axis gap-evaluation
     * aggregate. "No gaps found" is meaningful only when
     * `gapEvaluationAggregate.allClaimsCompleted` is true.
     */
    documentEvidenceSummary?: DocumentEvidenceSummary;
    /**
     * Section 10 (reformulated message) rewrite availability/receipt.
     * `available` requires a validation receipt matching the persisted
     * rewrite and canonical source; historical text with no valid receipt
     * projects as `unavailable:REWRITE_LEGACY_UNVERIFIED`.
     */
    section10RewriteState?: Section10RewriteState;
    /**
     * Corrected-document artifact status (distinct from Section 10 above —
     * this is the whole-document rewrite artifact's lease/generation
     * state, not the report's Section 10 excerpt).
     */
    correctedDocumentStatus?: CorrectedDocumentStatus;
    /**
     * Present only when `correctedDocumentStatus === "available"`. Binds
     * the corrected-text hash, input hash, and artifact epoch to the
     * sealed corrected-document provenance that produced it.
     */
    correctedDocumentReceipt?: CorrectedDocumentReceipt | null;
    /**
     * Immutable output-boundary provenance snapshot: source hash, resolved
     * context, ordered input manifest, model-operation occurrences,
     * feature-flag/config/model versions, legal-corpus revision, and build
     * revision. Never fabricated for historical rows — absent, not
     * inferred, when the run predates this spec.
     */
    outputProvenance?: OutputProvenance;
  };
}

/**
 * Failed assessment response
 */
export interface AssessmentStatusFailed extends AssessmentStatusCommon {
  status: "failed";
  errorMessage: string;
}

/**
 * Response from GET /api/assessments/:id
 */
export type AssessmentStatusResponse =
  | AssessmentStatusBase
  | AssessmentStatusCompleted
  | AssessmentStatusFailed;

/**
 * Assessment item in list view (summary, not full details)
 */
export interface AssessmentListItem {
  id: string;
  status: AssessmentStatus;
  progress: number;
  createdAt: string;
  auditId?: string;
  documentName?: string | null;
}

/**
 * Parameters for listing assessments with pagination and filters
 */
export interface AssessmentListParams {
  file_id?: string;
  page?: number;
  limit?: number;
}

/**
 * Response from GET /api/assessments with pagination
 */
export interface AssessmentListResponse {
  assessments: AssessmentListItem[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Research report status values
 */
export type ResearchReportStatus =
  | "queued"
  | "processing"
  | "completed"
  | "failed";

/**
 * Request for POST /api/research-reports
 */
export interface ResearchReportCreateRequest {
  prompt: string;
  session?: string;
  context?: Record<string, unknown>;
}

/**
 * Response from POST /api/research-reports
 */
export interface ResearchReportCreateResponse {
  reportId: string;
  status: "queued";
}

/**
 * Shared fields for research report status responses
 */
export interface ResearchReportStatusCommon {
  id: string;
  status: ResearchReportStatus;
  progress: number;
  createdAt: string;
  updatedAt: string;
  errorMessage?: string;
}

/**
 * Completed research report payload
 */
export interface ResearchReportCompleted extends ResearchReportStatusCommon {
  status: "completed";
  report: {
    markdown: string;
    json: Record<string, unknown>;
    /** Article 50(2) AI Act machine-readable content-provenance marker. */
    aiGenerated?: boolean;
    /**
     * Build/version-stamp: the git SHA the report was built from, for the
     * "App version: {hash}" footer sentence composed by
     * `report/build-provenance.ts`'s `getAppVersionText`. `null`/absent
     * when the build metadata wasn't available at generation time. See
     * docs/specs/report-build-version-stamp.md.
     */
    gitSha?: string | null;
  };
}

/**
 * Queued/processing/failed research report payload
 */
export interface ResearchReportPendingOrFailed
  extends ResearchReportStatusCommon {
  status: "queued" | "processing" | "failed";
}

/**
 * Response from GET /api/research-reports/:id
 */
export type ResearchReportStatusResponse =
  | ResearchReportCompleted
  | ResearchReportPendingOrFailed;

// Greenwashing Schema Integration Types (Phase 1)

/**
 * Medium type: where was the communication published (Step 1 context)
 */
export type MediumType =
  | "website"
  | "social_media"
  | "advertising"
  | "packaging"
  | "press_release"
  | "corporate_report"
  | "product_label"
  | "email_marketing"
  | "other";

/**
 * Target audience: who is the communication directed at
 */
export type TargetAudience =
  | "general_consumers"
  | "existing_customers"
  | "business_customers"
  | "internal_employees";

/**
 * Grey practices (7 types from Schemat - "7 Grzechów Greenwashingu")
 */
export type GreyPracticeType =
  | "vague_terminology" // Niejasność (Vagueness)
  | "misleading_imagery" // Wprowadzające w błąd obrazy/wizualizacje
  | "selective_disclosure" // Ukryty kompromis (Hidden Trade-off)
  | "false_certification_claim" // Fałszywe etykiety (Fake Labels)
  | "unverifiable_claim" // Brak dowodu (No Proof)
  | "irrelevant_claim" // Brak istotności (Irrelevance)
  | "hidden_trade_off"; // Mniejsze zło (Lesser of Two Evils)

/**
 * Black practices (12 types from UCPD Annex I + Green Claims Directive)
 */
export type BlackPracticeType =
  | "trust_mark_abuse" // Nadużycie znaku zaufania
  | "certification_fraud" // Oszustwo certyfikacyjne
  | "false_sustainability" // Fałszywe twierdzenia o zrównoważoności
  | "greenwashing_labels" // Zielone etykiety bez certyfikacji
  | "offsetting_deception" // Wprowadzanie w błąd dot. offsetów
  | "future_claims_abuse" // Nadużycie twierdzeń przyszłych
  | "bait_and_switch" // Przynęta i zamiana
  | "pyramid_scheme" // Schemat piramidy
  | "false_urgency" // Fałszywa pilność
  | "hidden_advertising" // Ukryta reklama
  | "endorsement_fraud" // Fałszywe rekomendacje
  | "prize_deception"; // Oszustwo nagrodowe

export type {
  BlackPracticeId,
  GreyPracticeId,
  PracticeTypeId,
} from "./practice-catalog.ts";

/**
 * Greenwashing risk classification (final assessment). PRD §5.1 3-tier scale;
 * synced with `greenwashingRiskEnum` (audits.ts) and `VALID_RISK` (schema.ts).
 */
export const GREENWASHING_RISK_VALUES = [
  "high_risk",
  "medium_risk",
  "low_risk",
] as const;

export type GreenwashingRisk = typeof GREENWASHING_RISK_VALUES[number];

/**
 * Declared communication purpose (intake). Whether the message is part of a
 * product sales/promotional campaign (`commercial`) or an educational/CSR/
 * public-awareness communication (`educational_csr`). Absent => unknown =>
 * analyze (ambiguity-default, scope-gate decision 3).
 */
export type CommunicationPurpose = "commercial" | "educational_csr";

/**
 * Confidence level emitted by the KROK 0 commercial-practice content classifier.
 */
export type ClassificationConfidence = "high" | "medium" | "low";

/**
 * Reason a pre-screening decision reached its `applicable` verdict.
 *
 * - `no_context_provided` / `all_gates_passed` / `not_consumer_directed` /
 *   `not_commercial_practice` / `unknown` — existing metadata-gate outcomes.
 * - `b2b_exempt` — binding B2B short-circuit (existing).
 * - `not_market_practice` — binding content-aware short-circuit: the text does
 *   not encourage a purchase / influence a transactional decision, so the
 *   anti-greenwashing regime (UCPD / 2024/825) does not apply (scope gate).
 * - `different_regime` — reserved for a future statutory-disclosure branch
 *   (ESG/CSRD), deferred out of v1 (scope-gate decision 8); listed here so the
 *   branch can be added without a signature change.
 * - `no_claims_extracted` — the text is in scope but claim extraction found
 *   no environmental claims, so there is nothing to assess; the audit
 *   completes with this reason instead of failing (adversarial case A15).
 */
export type PreScreeningReason =
  | "no_context_provided"
  | "all_gates_passed"
  | "not_consumer_directed"
  | "not_commercial_practice"
  | "b2b_exempt"
  | "not_market_practice"
  | "different_regime"
  | "no_claims_extracted"
  | "unknown";

/**
 * Verdict of the KROK 0 commercial-practice content classifier (§4.2). Reads the
 * message text (plus company background) and answers whether it is a commercial
 * practice — i.e. encourages purchase of / supports the sale of a product or
 * service, or otherwise plausibly influences a consumer's transactional decision.
 */
export interface CommercialPracticeClassification {
  isCommercialPractice: boolean;
  confidence: ClassificationConfidence;
  rationale: string;
}

/**
 * Verdict of the backend-only sector classifier (epic pirx-26iew.4,
 * ADR docs/adr/2026-08-04-legal-corpus-sector-filtering-nace-classifier.md).
 * Reads a company's `business_activity` free text and derives the
 * applicable NACE Rev. 2.1 division(s) (2-digit codes) for use as a
 * retrieval filter against the legal corpus's per-document `sectors`
 * field.
 *
 * `parentSection` is the section letter (e.g. "C") every `divisions`
 * entry's first character maps to. With a single-division verdict
 * this is the obvious parent; with a multi-division verdict (a
 * conglomerate company spanning sections) every `divisions` entry
 * SHOULD share `parentSection` — a cross-section multi-division
 * verdict is valid but unusual and the consumer should treat the
 * `divisions` array as authoritative.
 *
 * The classifier is constrained to the real NACE division enum; a
 * hallucinated code MUST NOT reach the retrieval filter (see
 * `services/legal-corpus/sector-classifier.ts`). Failures return
 * `null` (caller keeps retrieval at the widen-with-declared-only
 * fallback) rather than producing an empty/garbage verdict.
 */
export interface SectorClassification {
  divisions: string[];
  parentSection: string;
  confidence: ClassificationConfidence;
  rationale: string;
}

import type { MetaruleResult } from "./greenwashing/metarule.ts";

export { ALL_METARULE_CONDITION_CODES } from "./greenwashing/metarule.ts";
export type {
  MetaruleCondition,
  MetaruleConditionCode,
  MetaruleResult,
} from "./greenwashing/metarule.ts";

// Per-claim rule-match output contract (PRD §5.7, pirx-bhwb4).
// The per-claim collision comparator (pirx-sdbry) consumes
// `PerClaimCollisionInput`; this is the single source of truth for
// the input/output shape across the shared/backend boundary.
// Spec: `docs/specs/per-claim-collision-comparator.md`.
export type {
  MetaruleConditionForCollision,
  PerClaimCollisionInput,
  PerClaimCollisionResult,
  RuleMatch,
  RuleMatchSeverity,
} from "./greenwashing/rule-match.ts";
// Group A's structured legal-basis strings (pirx-z0mic.6,
// risk-assessment-precision-lost-downstream) — a distinct channel from the
// `ecolabelStatus` signal threaded separately into `generate_recommendation`
// (docs/specs/recommendation-generation-safety-fabrication.md).
export type {
  LegalBasisScenario,
  LegalBasisStringEntry,
} from "./report/legal-basis-strings.ts";

/**
 * Pre-screening result from TypeScript pre-computation.
 *
 * Diagnostic fields (`declaredPurpose`, `contentIsCommercialPractice`,
 * `contentConfidence`, `purposeDisagreement`) are populated when the
 * content-aware scope gate runs; all are optional and back-compatible.
 *
 * `metaruleResult` is the PRD §5.2 four-predicate classification. It is
 * persisted unconditionally on every audit row (including B2B-exempt and
 * editorial-medium cases where `applicable` is false or `lit-d` fails) —
 * it documents UCPD art. 2 legal-basis applicability per PRD §5.2, which
 * is an INDEPENDENT question from the methodology policy recorded in
 * `applicable` / `reason` / `gatesPassed`. Phase 3 (.10) renders the
 * legal-basis array per failed metarule condition regardless of the
 * methodology gate's verdict.
 *
 * STALENESS CAVEAT (pirx-yxa6i): `applicable` / `reason` / `gatesPassed`
 * are the METADATA-ONLY intake verdict (`checkPreScreening()`).
 * The analyzer's checkWithContent step (`checkPreScreeningWithContent()`,
 * pre-screening.ts:196) can BINDINGLY flip `applicable` to false via the
 * content short-circuit; that verdict is NOT written back. `metaruleResult`
 * is the only intake-authoritative field on this shape.
 */
export interface PreScreeningResult {
  /**
   * STALENESS CAVEAT: INTAKE-ONLY SNAPSHOT. Do NOT consume without
   * recomputing via the analyzer's checkWithContent step
   * (`checkPreScreeningWithContent()`) — the analyzer at analyze-time
   * can bindingly flip this to false via the `not_market_practice`
   * content short-circuit, and that post-content verdict is NOT
   * written back to the jsonb column. Future consumers must recompute
   * OR read `metaruleResult` instead (intake-authoritative; PRD §5.2
   * deterministic from `AuditContextRequest`).
   */
  applicable: boolean;
  /**
   * STALENESS CAVEAT: Same staleness caveat as `applicable`. Recompute
   * via `checkPreScreeningWithContent()` if needed.
   */
  reason?: PreScreeningReason;
  /**
   * STALENESS CAVEAT: Same staleness caveat as `applicable`.
   * `economicInterest` is a derived mirror of Gates 1+2 by construction
   * (see pre-screening.ts:113-119), never independent; the consumer-only
   * signal of interest is `metaruleResult.metaruleFailedConditions`.
   */
  gatesPassed: {
    consumerDirected: boolean;
    commercialPractice: boolean;
    economicInterest: boolean;
  };
  /** Declared communication purpose from intake, when the user supplied it. */
  declaredPurpose?: CommunicationPurpose;
  /** Content classifier verdict: whether the text is a commercial practice. */
  contentIsCommercialPractice?: boolean;
  /** Content classifier confidence in that verdict. */
  contentConfidence?: ClassificationConfidence;
  /**
   * True when the declared purpose disagrees with the content classifier
   * (e.g. declared `educational_csr` but the text reads as commercial). Recorded
   * and surfaced, never used to silently override (§4.3).
   */
  purposeDisagreement?: boolean;
  /** PRD §5.2 four-predicate metarule classification (authoritative for Phase 3 legal-basis rendering). */
  metaruleResult?: MetaruleResult;
  /** Server-stamped D2 override record when honored (FF + allowlist). */
  csrOverrideAcknowledgement?: CsrOverrideAcknowledgement;
  /**
   * Declared-vs-detected `sectors` disagreement (pirx-26iew.6, schema
   * scoped by pirx-8wdz1). Populated for audits that ran the sector
   * classifier (pirx-26iew.4) or, in its absence, for every audit
   * (declared side is always populated; detected side is empty when
   * the classifier didn't run). Consumed by the transparency page's
   * `sectorsDisagreement` field via the
   * `pipelineTransparencyAuditContextSummarySchema` mirror in
   * `pipeline-transparency.ts`. See that schema for the canonical
   * field shape (`SectorsDisagreement`).
   */
  sectorsDisagreement?: SectorsDisagreement;
}

/**
 * Audit context (Step 1 metadata) for greenwashing analysis
 */
export interface AuditContext {
  id: string;
  auditId: string;
  mediumType: MediumType;
  targetAudience: TargetAudience;
  /**
   * Declared communication purpose (§4.1). Absent => unknown => analyze
   * (ambiguity-default). A prior for the scope gate, not an override.
   */
  communicationPurpose?: CommunicationPurpose;
  industrySector?: string;
  geographicScope?: string;
  publicationDate?: string;
  preScreeningResult?: PreScreeningResult;
  createdAt: string;
}

/**
 * Canonical target-market options. Stored verbatim (Polish display strings) so
 * a chosen default seeds the free-text `audit_context.geographic_scope` it maps
 * to. `users.default_market` is validated against this set at the API layer.
 */
export const MARKET_OPTIONS = [
  "Polska",
  "Unia Europejska",
  "Niemcy",
  "Francja",
] as const;
export type Market = (typeof MARKET_OPTIONS)[number];

/** Client wire shape for D2 CSR override ack (no client-supplied ackAt). */
export interface UserAckedCsrDespiteContentCommercialWire {
  reason?: string;
}

/** Persisted CSR override ack with server-stamped metadata. */
export interface CsrOverrideAcknowledgement {
  reason?: string;
  csrOverrideAcknowledgedAt: string;
  csrOverrideAcknowledgedBy: string;
}

/** Report commentary / analysis mode axis (D8). */
export type AnalysisModeAxis = "commercial" | "csr";

/**
 * Request body for creating audit with context
 */
export interface AuditContextRequest {
  mediumType: MediumType;
  targetAudience: TargetAudience;
  /**
   * Declared communication purpose (§4.1). Optional; absent => unknown =>
   * analyze (ambiguity-default). A prior for the scope gate, not an override.
   */
  communicationPurpose?: CommunicationPurpose;
  /**
   * Vulnerable-audience signal (KROK1 §1.2 / audit finding §3.2): flags that
   * the target audience includes a "particularly susceptible" consumer group
   * (children, elderly, impaired, credulous) requiring a higher protection
   * standard. Optional; absent/false => average-consumer standard applies.
   */
  vulnerableAudience?: boolean;
  industrySector?: string;
  geographicScope?: string;
  publicationDate?: string;
  /**
   * D2: user acknowledges CSR mode despite content reading as commercial.
   * Wire shape only — server stamps ackAt/ackBy at persistence.
   */
  userAckedCsrDespiteContentCommercial?:
    UserAckedCsrDespiteContentCommercialWire;
  /**
   * D8 analysis mode axis. Defaults to commercial when omitted.
   */
  mode?: AnalysisModeAxis;
}

// Phase 8: Audit Extensions, Teams, Billing, GDPR, Auth Types

/**
 * Download response for binary endpoints
 */
export interface DownloadResponse {
  data: Uint8Array;
  filename?: string;
  contentType?: string;
}

/**
 * Upload-and-analyze batch audit response
 */
export interface UploadAndAnalyzeResult {
  auditId: string;
  fileName: string;
  status: string;
}

export interface UploadAndAnalyzeResponse {
  audits: UploadAndAnalyzeResult[];
}

/**
 * Result of submitting text for analysis
 */
export interface SubmitTextResult {
  auditId: string;
  fileName: string;
  status: "QUEUED";
}

/**
 * Request payload for text submission. Shared between frontend, backend
 * service layer, and backend route handlers so the wire contract has one
 * canonical shape.
 */
export interface SubmitTextRequest {
  text: string;
  title?: string;
  context?: AuditContextRequest;
  language?: "pl" | "en";
}

/**
 * Response from text submission endpoint
 */
export interface SubmitTextResponse {
  audit: SubmitTextResult;
}

/**
 * URL-scan fail-loud states surfaced to the client (pirx-27tn). Mirrors the
 * backend `UrlScanFailureCode`; the frontend maps each to an i18n message.
 */
export type UrlScanFailureCode =
  | "LOGIN_WALL"
  | "BLOCKED"
  | "ROBOTS_DISALLOWED"
  | "EMPTY_AFTER_EXTRACTION"
  | "FETCH_ERROR";

/**
 * Request payload for URL scan (pirx-27tn). The third input mode alongside
 * paste-text and upload-file. `attestation` is the Tier-A gate (the user
 * affirms the right to audit the target). Shares the success response shape
 * (`SubmitTextResponse`) with text submission.
 */
export interface SubmitUrlRequest {
  url: string;
  attestation: boolean;
  context?: AuditContextRequest;
  language?: "pl" | "en";
}

/**
 * Audit deletion responses
 */
export interface AuditDeleteResponse {
  success: boolean;
  deletedAuditId: string;
}

export interface AuditBulkDeleteResponse {
  success: boolean;
  deletedCount: number;
  failedFileDeletes: number;
}

/**
 * Feedback request/response types
 */
export interface SubmitFeedbackRequest {
  rating: number;
  comment?: string;
}

export interface SubmitFeedbackResponse {
  id: string;
  auditId: string;
  rating: number;
  comment?: string | null;
  createdAt: string;
  updatedAt: string;
  updated: boolean;
}

export interface GetFeedbackResponse {
  id: string;
  auditId: string;
  rating: number;
  comment?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Auth endpoints
 */
/**
 * Authentication method used, captured by middleware.
 */
export type AuthMethod =
  | "cli-service-token"
  | "session-cookie"
  | "firebase"
  | "firebase-unregistered"
  | "firebase-only";

export interface AuthRegisterRequest {
  name?: string;
}

export interface AuthRegisterResponse {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
  teams: TeamInfo[];
  isNew: boolean;
}

export interface AuthCheckResponse {
  authenticated: boolean;
  authMethod?: AuthMethod;
}

export interface AuthUpdateRequest {
  name?: string;
  avatarUrl?: string;
}

export interface AuthUpdateResponse {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
  teams: TeamInfo[];
}

/**
 * Team endpoints
 */
export type TeamRole = "owner" | "admin" | "member";

export interface TeamSummary {
  id: string;
  name: string;
  role: TeamRole;
}

export interface TeamListResponse {
  teams: TeamSummary[];
}

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
  role: TeamRole;
  acceptedAt?: string | null;
}

export interface TeamDetailResponse {
  id: string;
  name: string;
  ownerId: string;
  createdAt: string;
  currentUserRole?: TeamRole;
  members: TeamMember[];
}

export interface TeamCreateRequest {
  name: string;
}

export interface TeamCreateResponse {
  id: string;
  name: string;
  ownerId: string;
  createdAt: string;
  role: TeamRole;
}

export interface TeamUpdateRequest {
  name?: string;
}

export interface TeamUpdateResponse {
  id: string;
  name: string;
  ownerId: string;
  updatedAt: string;
}

export interface TeamDeleteResponse {
  success: boolean;
  deletedTeamId: string;
}

export interface TeamAddMemberRequest {
  email: string;
  role?: TeamRole;
}

export interface TeamAddMemberResponse {
  id: string;
  email: string;
  name: string;
  role: TeamRole;
  acceptedAt?: string | null;
}

export interface TeamUpdateMemberRequest {
  role: TeamRole;
}

export interface TeamUpdateMemberResponse {
  userId: string;
  teamId: string;
  role: TeamRole;
}

export interface TeamRemoveMemberResponse {
  success: boolean;
  removedUserId: string;
}

/**
 * Billing endpoints (extended)
 */
export interface BillingCreditsResponse {
  balance: number;
  lastUpdated?: string | null;
  creditUnitChars: number;
}

export interface BillingProformaResponse {
  orderNumber: string;
  creditAmount: number;
  netAmount: number;
  totalAmount: number;
  totalWithVat: number;
  currency: string;
  pdf: string;
}

export interface BillingCreditsAddRequest {
  amount: number;
  orderNumber?: string | null;
  email?: string; // Required in admin top-up mode to identify target user
}

export interface BillingCreditsAddResponse {
  success: boolean;
  newBalance: number;
  creditsAdded: number;
  orderNumber?: string | null;
}

export interface BillingCreditsSetRequest {
  balance: number;
  email: string;
}

export interface BillingCreditsSetResponse {
  success: boolean;
  newBalance: number;
}

export interface BillingCreditsDeductRequest {
  amount: number;
  auditId?: string | null;
  email: string;
}

export type BillingCreditsDeductResponse =
  | {
    success: true;
    newBalance: number;
    creditsDeducted: number;
    auditId?: string | null;
  }
  | {
    success: false;
    reason: "insufficient_credits";
    currentBalance: number;
    required: number;
  };

export interface BillingWebhookResponse {
  received: boolean;
  duplicate?: boolean;
}

/**
 * GDPR endpoints
 */
export interface GdprExportResponse {
  exportedAt: string;
  userId: string;
  dataRetentionPeriod: string;
  auditLogs: Array<{
    auditId: string;
    userId: string;
    inputTextHash: string | null;
    characterCount: number | null;
    unitCount: number | null;
    resultSummary: string | null;
    processingTimeMs: number | null;
    aiModelVersion: string | null;
    methodologyVersion: string | null;
    createdAt: string;
  }>;
  metadata: Record<string, unknown>;
}

export interface GdprDeleteResponse {
  success: boolean;
  deletedAt: string;
  affectedSources: string[];
  recordsAffected: number;
  deletionType: string;
  notes?: Record<string, string>;
}

// Admin Endpoints (Internal - require x-internal-secret)

/**
 * Admin user info returned by admin endpoints
 */
export interface AdminUserInfo {
  id: string;
  email: string;
  name: string | null;
  accountEnabled: boolean;
  // Present in GET /api/admin/users/status responses.
  onboardingCompleted?: boolean;
  createdAt?: string;
}

/**
 * Response from POST /api/admin/users/enable
 */
export interface AdminUserEnableResponse {
  success: boolean;
  user: AdminUserInfo;
}

/**
 * Response from POST /api/admin/users/disable
 */
export interface AdminUserDisableResponse {
  success: boolean;
  user: AdminUserInfo;
}

/**
 * Response from GET /api/admin/users/status
 */
export interface AdminUserStatusResponse {
  user: AdminUserInfo;
}

/**
 * A user awaiting activation (GET /api/admin/users/pending). See pirx-wyk9.
 */
export interface AdminPendingUser {
  bucket: "firebase_only" | "disabled";
  email: string | null;
  firebaseUid: string | null;
  name: string | null;
  provider: string | null;
  emailVerified: boolean | null;
  createdAt: string | null;
  looksSynthetic: boolean;
}

/**
 * Response from GET /api/admin/users/pending
 */
export interface AdminPendingUsersResponse {
  pending: AdminPendingUser[];
  counts: { firebaseOnly: number; disabled: number };
}

/**
 * One row of the user activity report (GET /api/admin/users/report): an app-DB
 * user and how many audits (jobs) they own. See pirx-v4p50.
 */
export interface AdminUserActivityRow {
  email: string;
  name: string | null;
  jobs: number;
  accountEnabled: boolean;
  createdAt: string;
  /** Known internal/team/service account (exact-email match). */
  internal: boolean;
}

/**
 * Response from GET /api/admin/users/report
 */
export interface AdminUsersReportResponse {
  users: AdminUserActivityRow[];
  counts: { customers: number; internal: number; total: number };
}

/**
 * Response from POST /api/admin/users/activate
 */
export interface AdminUserActivateResponse {
  success: boolean;
  provisioned: boolean;
  emailVerifiedSet: boolean;
  user: AdminUserInfo;
}

/**
 * Response from POST /api/admin/users/rotate-credentials (pirx-sajc8).
 * The rotated password is never returned by the server; the caller (CLI) knows
 * the value it sent.
 */
export interface AdminUserRotateCredentialsResponse {
  email: string;
  firebaseUid: string;
  /** True if the Firebase user was created by this call (create-if-missing). */
  created: boolean;
}

/**
 * Deletion counts returned by user delete endpoint
 */
export interface UserDeletionCounts {
  user: boolean;
  billingAccount: boolean;
  firebaseAuth: boolean;
  auditLogs: number;
  audits: number;
  kbFiles: number;
  teams: number;
}

/**
 * Response from POST /api/admin/users/delete
 */
export interface AdminUserDeleteResponse {
  success: boolean;
  userId: string;
  firebaseUid: string;
  email: string;
  deleted: UserDeletionCounts;
  warnings: string[];
}

/**
 * LLM operation type enum
 */
export type LlmOperationType =
  | "extract_claims"
  | "rag_query"
  | "assess_risk"
  | "generate_recommendation"
  | "detect_gaps"
  | "match_evidence"
  | "generate_messaging";

/**
 * LLM interaction record for observability
 */
export interface LlmInteraction {
  id: string;
  auditId: string | null;
  userId: string | null;
  operation: LlmOperationType;
  modelName: string;
  requestPrompt: string;
  responseRaw: string;
  responseParsed: unknown;
  responseFinishReason: string | null;
  errorMessage: string | null;
  latencyMs: number;
  metadata: Record<string, unknown>;
  createdAt: string;
}

/**
 * Response from GET /api/admin/llm-interactions
 */
export interface AdminLlmInteractionsResponse {
  interactions: LlmInteraction[];
  total: number;
}

/**
 * Response from GET /api/admin/llm-interactions/:id
 */
export interface AdminLlmInteractionResponse {
  interaction: LlmInteraction;
}

/**
 * One queue's health snapshot (GET /api/admin/queues/health, pirx-wg2xa.5).
 * `oldestPendingAgeSeconds` is the age in whole seconds of the oldest
 * in-flight lease's start — null when no row is in-flight.
 * `staleLeaseCount` is the row count a stale-lease reaper would reclaim
 * now — null for the monitoring surface, which has no lease-age predicate.
 */
export interface QueueHealthSnapshot {
  depthQueued: number;
  depthProcessing: number;
  oldestPendingAgeSeconds: number | null;
  staleLeaseCount: number | null;
}

/**
 * Response from GET /api/admin/queues/health: one sub-object per queue
 * surface (audits, assessmentQueue, auditRagQueue, researchQueue,
 * monitoredSources).
 */
export interface AdminQueuesHealthResponse {
  audits: QueueHealthSnapshot;
  assessmentQueue: QueueHealthSnapshot;
  auditRagQueue: QueueHealthSnapshot;
  researchQueue: QueueHealthSnapshot;
  monitoredSources: QueueHealthSnapshot;
}
